import { Spine } from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";
import anime from "animejs";
import { setShowRoyalCollectOutro, setRoyalCollectOutroTransitionActive } from "../../../../State/actions";
import { Settings } from "../../../../settings";
import { PixiSettings } from "../../../../pixiSettings";
import { soundManager } from "../../../../SoundManager";
import { SOUND_NAME } from "../../../../sounds";

// Device and orientation-specific size configuration
// Reuses same layout as FreeSpinOutroDialog since they share the same spine asset
const OUTRO_SIZE_CONFIG = {
  MOBILE: {
    PORTRAIT: {
      spineScaleMultiplier: 1.0,
      spineYOffset: 0,
      buttonYOffsetMultiplier: 0.85,
      buttonFontScale: 0.5,
    },
    LANDSCAPE: {
      spineScaleMultiplier: 1.05,
      spineYOffset: 0,
      buttonYOffsetMultiplier: 0.75,
      buttonFontScale: 0.65,
    },
  },
  TABLET: {
    PORTRAIT: {
      spineScaleMultiplier: 1.2,
      spineYOffset: 0,
      buttonYOffsetMultiplier: 1.5,
      buttonFontScale: 0.65,
    },
    LANDSCAPE: {
      spineScaleMultiplier: 1.0,
      spineYOffset: 0,
      buttonYOffsetMultiplier: 1.5,
      buttonFontScale: 0.65,
    },
  },
  DESKTOP: {
    LANDSCAPE: {
      spineScaleMultiplier: 1.0,
      spineYOffset: 0,
      buttonYOffsetMultiplier: 1.0,
      buttonFontScale: 1.1,
    },
  },
};

/**
 * RoyalCollectOutroDialogController
 * Handles the Royal Collect outro dialog: in → idle → (user click) → out
 * Mirrors FreeSpinOutroDialogController exactly, with RC-specific dispatch actions.
 * Text slots: txt_slot_total_win, txt_slot_total_value
 */
class RoyalCollectOutroDialogController {
  constructor(
    container,
    dimensionsContext,
    dispatch,
    symbolHeight,
    overlayRef,
    buttonRef,
    t,
  ) {
    this.container = container;
    this.dimensionsContext = dimensionsContext;
    this.dispatch = dispatch;
    this.symbolHeight = symbolHeight;
    this.overlayRef = overlayRef;
    this.buttonRef = buttonRef;
    this.t = t;

    this.dimensions = dimensionsContext.dimensions;

    // Spine instance
    this.outroSpine = null;
    this.isPlaying = false;
    this.initialized = false;
    this.isWaitingForClick = false;

    // User interaction state
    this.canSkipAnimation = false;
    this.isHandlingUserClick = false;
    this.debounceTimeout = null;
    this.skipProtectionTimeout = null;
    this.currentTimeout = null;

    // Button
    this.buttonContainer = null;
    this.buttonShadowText = null;
    this.buttonText = null;

    // Text containers for cleanup
    this.textContainers = null;

    // Internal caches
    this._configCache = null;
    this._availableAnimationsCache = null;
    this._lastDevice = null;
    this._lastOrientation = null;

    this.initializeOutroSpine();
  }

  // ===================================================================
  //  CONFIG / SCALING
  // ===================================================================

  getConfig() {
    const { device, orientation } = this.dimensionsContext;

    if (
      this._configCache !== null &&
      this._lastDevice === device &&
      this._lastOrientation === orientation
    ) {
      return this._configCache;
    }

    let deviceKey;
    if (device === PixiSettings.DEVICE.MOBILE) deviceKey = "MOBILE";
    else if (device === PixiSettings.DEVICE.TABLET) deviceKey = "TABLET";
    else deviceKey = "DESKTOP";

    const orientationKey =
      orientation === PixiSettings.ORIENTATION.PORTRAIT
        ? "PORTRAIT"
        : "LANDSCAPE";

    const deviceConfig =
      OUTRO_SIZE_CONFIG[deviceKey] || OUTRO_SIZE_CONFIG.DESKTOP;
    const config =
      deviceConfig[orientationKey] ||
      deviceConfig.LANDSCAPE ||
      OUTRO_SIZE_CONFIG.DESKTOP.LANDSCAPE;

    this._configCache = config;
    this._lastDevice = device;
    this._lastOrientation = orientation;
    return config;
  }

  applyScaleAndCenter() {
    if (!this.outroSpine || !this.dimensions) return;

    const config = this.getConfig();
    const nativeSize = Settings.SYMBOL_HEIGHT;
    const targetSize = this.symbolHeight || nativeSize;
    let scale = Math.max(0.1, Math.min(targetSize / nativeSize, 2));
    scale *= config.spineScaleMultiplier;

    this.outroSpine.scale.set(scale);
    this.outroSpine.x = 0;
    this.outroSpine.y = config.spineYOffset || 0;

    this.updateButtonPosition();
  }

  updateDimensions(dimensionsContext, symbolHeight) {
    const oldDimensions = this.dimensionsContext?.dimensions;
    const newDimensions = dimensionsContext?.dimensions;
    const oldDevice = this.dimensionsContext?.device;
    const newDevice = dimensionsContext?.device;

    const dimensionsChanged =
      !oldDimensions ||
      oldDimensions.width !== newDimensions?.width ||
      oldDimensions.height !== newDimensions?.height ||
      this.dimensionsContext?.device !== dimensionsContext?.device ||
      this.dimensionsContext?.orientation !== dimensionsContext?.orientation;

    const symbolHeightChanged = this.symbolHeight !== symbolHeight;
    const deviceChanged = oldDevice !== newDevice;

    if (!dimensionsChanged && !symbolHeightChanged) return;

    this.dimensionsContext = dimensionsContext;
    this.dimensions = dimensionsContext.dimensions;
    this.symbolHeight = symbolHeight;

    if (
      deviceChanged ||
      this._lastDevice !== dimensionsContext.device ||
      this._lastOrientation !== dimensionsContext.orientation
    ) {
      this._configCache = null;
      this._lastDevice = dimensionsContext.device;
      this._lastOrientation = dimensionsContext.orientation;
    }

    if (deviceChanged && this.buttonText) {
      this.updateButtonText();
    }
    this.updateButtonStyle();

    if (
      this.outroSpine &&
      this.dimensions &&
      (dimensionsChanged || symbolHeightChanged)
    ) {
      if (this.symbolHeight && this.symbolHeight > 0) {
        this.applyScaleAndCenter();
      }
    }
  }

  // ===================================================================
  //  SPINE CREATION
  // ===================================================================

  initializeOutroSpine() {
    try {
      this.outroSpine = this.createOutroSpine();

      if (!this.outroSpine) return;

      const missingComponents = [];
      if (!this.outroSpine.skeleton) missingComponents.push("skeleton");
      if (!this.outroSpine.state) missingComponents.push("state");
      if (!this.outroSpine.skeleton?.data)
        missingComponents.push("skeleton.data");

      if (missingComponents.length > 0) return;

      this.container.addChild(this.outroSpine);
      this.outroSpine.alpha = 0.001;
      this.outroSpine.zIndex = 20;

      this.applyScaleAndCenter();

      if (this.outroSpine.parent !== this.container) return;

      this.initialized = true;
      this._availableAnimationsCache = null;

      this.warmUpOutroSpine();
      this.initializeButton();
      this.initializeTexts();
    } catch {
      // Silent fail
    }
  }

  createOutroSpine() {
    try {
      const assets = PIXI.Assets.cache;
      const skeletonAlias = "dialogs-skeleton";
      const atlasAlias = "dialogs-atlas";

      if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) return null;

      const outroSpine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });

      if (!outroSpine?.skeleton?.data?.animations) return null;

      return outroSpine;
    } catch {
      return null;
    }
  }

  warmUpOutroSpine() {
    if (!this.outroSpine?.state) return;
    try {
      const animations = this.getAvailableAnimations();
      if (animations.length > 0) {
        this.outroSpine.state.setAnimation(0, animations[0], false);
        this.outroSpine.state.update(0.016);
        this.outroSpine.skeleton.updateWorldTransform();
      }
      this.outroSpine.alpha = 0.001;
    } catch {
      // Silent fail
    }
  }

  getAvailableAnimations() {
    if (!this.outroSpine?.skeleton?.data?.animations) return [];
    if (this._availableAnimationsCache !== null)
      return this._availableAnimationsCache;

    const animations = this.outroSpine.skeleton.data.animations.map(
      (a) => a.name,
    );
    this._availableAnimationsCache = animations;
    return animations;
  }

  // ===================================================================
  //  BUTTON
  // ===================================================================

  initializeButton() {
    if (!this.outroSpine || !this.outroSpine.skeleton) return;

    this.buttonContainer = new PIXI.Container();
    this.buttonContainer.name = "royal_collect_outro_continue_overlay";
    this.buttonContainer.alpha = 0.001;
    this.buttonContainer.zIndex = 1001;

    const translationKey =
      this.dimensionsContext?.device === PixiSettings.DEVICE.DESKTOP
        ? "click_to_continue"
        : "tap_to_continue";
    const buttonText = this.t(translationKey).toUpperCase();

    const primaryStyle = PixiSettings.FONT_STYLES.PRIMARY;
    const slotText = PixiSettings.FONT_STYLES.SLOT_TEXT;
    const config = this.getConfig();
    const fontSize = Math.round(32 * (config.buttonFontScale || 1.0));
    const scaled = this._computeScaledStrokeParams(fontSize);
    const sharedProps = {
      fontFamily: primaryStyle.fontFamily,
      ...slotText,
      fontSize,
      fontWeight: "bold",
    };

    this.buttonShadowText = new PIXI.Text(
      buttonText,
      new PIXI.TextStyle({
        ...sharedProps,
        ...primaryStyle.shadow,
        strokeThickness: scaled.shadowStroke,
      }),
    );
    this.buttonShadowText.resolution = slotText.resolution;
    this.buttonShadowText.anchor.set(0.5);
    this.buttonShadowText.y = scaled.shadowOffsetY;

    this.buttonText = new PIXI.Text(
      buttonText,
      new PIXI.TextStyle({
        ...sharedProps,
        ...primaryStyle.top,
        strokeThickness: scaled.topStroke,
      }),
    );
    this.buttonText.resolution = slotText.resolution;
    this.buttonText.anchor.set(0.5);

    this.buttonContainer.addChild(this.buttonShadowText);
    this.buttonContainer.addChild(this.buttonText);
    this.container.addChild(this.buttonContainer);

    this.updateButtonPosition();
  }

  updateButtonPosition() {
    if (!this.buttonContainer || !this.dimensions) return;
    const config = this.getConfig();
    const multiplier = config.buttonYOffsetMultiplier ?? 1.0;
    const buttonY = this.dimensions.height / 2 - this.symbolHeight * multiplier;
    this.buttonContainer.x = 0;
    this.buttonContainer.y = buttonY;
  }

  updateButtonStyle() {
    if (!this.buttonText || !this.buttonShadowText) return;

    const config = this.getConfig();
    const primaryStyle = PixiSettings.FONT_STYLES.PRIMARY;
    const slotText = PixiSettings.FONT_STYLES.SLOT_TEXT;
    const fontSize = Math.round(32 * (config.buttonFontScale || 1.0));
    const scaled = this._computeScaledStrokeParams(fontSize);

    this.buttonShadowText.style = new PIXI.TextStyle({
      fontFamily: primaryStyle.fontFamily,
      ...slotText,
      fontSize,
      fontWeight: "bold",
      ...primaryStyle.shadow,
      strokeThickness: scaled.shadowStroke,
    });
    this.buttonShadowText.y = scaled.shadowOffsetY;

    this.buttonText.style = new PIXI.TextStyle({
      fontFamily: primaryStyle.fontFamily,
      ...slotText,
      fontSize,
      fontWeight: "bold",
      ...primaryStyle.top,
      strokeThickness: scaled.topStroke,
    });
  }

  _computeScaledStrokeParams(fontSize) {
    const baseFontSize = 32;
    const ratio = Math.max(0.3, fontSize / baseFontSize);
    const primaryStyle = PixiSettings.FONT_STYLES.PRIMARY;
    return {
      shadowStroke: Math.max(1, Math.round((primaryStyle.shadow.strokeThickness || 4) * ratio)),
      topStroke: Math.max(1, Math.round((primaryStyle.top.strokeThickness || 3) * ratio)),
      shadowOffsetY: Math.max(1, Math.round((primaryStyle.shadowOffsetY || 3) * ratio)),
    };
  }

  updateButtonText() {
    if (!this.buttonText) return;
    const translationKey =
      this.dimensionsContext?.device === PixiSettings.DEVICE.DESKTOP
        ? "click_to_continue"
        : "tap_to_continue";
    const buttonText = this.t(translationKey).toUpperCase();
    this.buttonText.text = buttonText;
    if (this.buttonShadowText) this.buttonShadowText.text = buttonText;
  }

  showButton() {
    if (this.buttonContainer) {
      anime({
        targets: this.buttonContainer,
        alpha: 1,
        duration: 300,
        easing: "easeOutQuad",
      });
    }
    if (this.buttonRef?.current) this.buttonRef.current.alpha = 0;
  }

  hideButton() {
    if (this.buttonContainer) {
      anime({
        targets: this.buttonContainer,
        alpha: 0,
        duration: 300,
        easing: "easeOutQuad",
      });
    }
    if (this.buttonRef?.current) this.buttonRef.current.alpha = 0;
  }

  // ===================================================================
  //  TEXT SLOTS
  // ===================================================================

  initializeTexts() {
    if (!this.outroSpine?.skeleton) return;

    const primaryStyle = PixiSettings.FONT_STYLES.PRIMARY;
    const secondaryStyle = PixiSettings.FONT_STYLES.SECONDARY;
    const slotText = PixiSettings.FONT_STYLES.SLOT_TEXT;

    // TOTAL WIN — PRIMARY two-layer
    this.initializePrimarySlotText(
      "txt_slot_total_win",
      this.t("total_win").toUpperCase(),
      primaryStyle,
      slotText,
      76,
      "800",
    );

    // Total win value — SECONDARY, placeholder until updateTotalWinValue is called
    this.totalValueText = this.initializeSecondarySlotText(
      "txt_slot_total_value",
      "",
      secondaryStyle,
      slotText,
      86,
    );
  }

  initializePrimarySlotText(slotName, text, primaryStyle, slotText, fontSize, fontWeight = "800") {
    const slot = this.outroSpine.skeleton.findSlot(slotName);
    if (!slot) return;

    const container = new PIXI.Container();
    container.name = `${slotName}_text_attached_to_slot`;
    container.zIndex = 1000;

    const sharedProps = {
      fontFamily: primaryStyle.fontFamily,
      ...slotText,
      fontSize,
      fontWeight,
    };

    const shadowText = new PIXI.Text(
      text,
      new PIXI.TextStyle({ ...sharedProps, ...primaryStyle.shadow }),
    );
    shadowText.resolution = 2;
    shadowText.anchor.set(0.5);
    shadowText.y = primaryStyle.shadowOffsetY || 3;

    const topText = new PIXI.Text(
      text,
      new PIXI.TextStyle({ ...sharedProps, ...primaryStyle.top }),
    );
    topText.resolution = 2;
    topText.anchor.set(0.5);

    container.addChild(shadowText);
    container.addChild(topText);
    this.outroSpine.addSlotObject(slotName, container);

    if (!this.textContainers) this.textContainers = [];
    this.textContainers.push(container);
  }

  initializeSecondarySlotText(slotName, text, secondaryStyle, slotText, fontSize) {
    const slot = this.outroSpine.skeleton.findSlot(slotName);
    if (!slot) return;

    const container = new PIXI.Container();
    container.name = `${slotName}_text_attached_to_slot`;
    container.zIndex = 1000;

    const textObj = new PIXI.Text(
      text,
      new PIXI.TextStyle({
        fontFamily: secondaryStyle.fontFamily,
        fill: secondaryStyle.fill,
        fillGradientType: secondaryStyle.fillGradientType,
        stroke: secondaryStyle.stroke,
        strokeThickness: secondaryStyle.strokeThickness,
        dropShadow: secondaryStyle.dropShadow,
        dropShadowColor: secondaryStyle.dropShadowColor,
        dropShadowDistance: secondaryStyle.dropShadowDistance,
        dropShadowBlur: secondaryStyle.dropShadowBlur,
        dropShadowAlpha: secondaryStyle.dropShadowAlpha,
        dropShadowAngle: secondaryStyle.dropShadowAngle,
        ...slotText,
        fontSize,
        fontWeight: "700",
      }),
    );
    textObj.resolution = 2;
    textObj.anchor.set(0.5);

    container.addChild(textObj);
    this.outroSpine.addSlotObject(slotName, container);

    if (!this.textContainers) this.textContainers = [];
    this.textContainers.push(container);

    return textObj;
  }

  updateTotalWinValue(text) {
    if (this.totalValueText) {
      this.totalValueText.text = text;
    }
  }

  // ===================================================================
  //  ANIMATION HELPERS
  // ===================================================================

  getSpineAnimationDuration(animationName) {
    if (!this.outroSpine?.skeleton?.data?.animations) return 1000;
    try {
      const animation = this.outroSpine.skeleton.data.animations.find(
        (anim) => anim.name === animationName,
      );
      if (animation && animation.duration) {
        return Math.round(animation.duration * 1000);
      }
      return 1000;
    } catch {
      return 1000;
    }
  }

  // ===================================================================
  //  ANIMATION SEQUENCE
  // ===================================================================

  restartSequence() {
    if (this.isWaitingForClick) return;
    if (!this.isPlaying) return;

    if (this.currentTimeout) { clearTimeout(this.currentTimeout); this.currentTimeout = null; }
    if (this.skipProtectionTimeout) { clearTimeout(this.skipProtectionTimeout); this.skipProtectionTimeout = null; }
    if (this.debounceTimeout) { clearTimeout(this.debounceTimeout); this.debounceTimeout = null; }

    this.isPlaying = false;
    this.isWaitingForClick = false;
    this.canSkipAnimation = false;
    this.isHandlingUserClick = false;

    this.startSequence();
  }

  startSequence() {
    return new Promise((resolve) => {
      if (this.isPlaying || !this.outroSpine?.state) {
        resolve();
        return;
      }

      this.isPlaying = true;
      this.isWaitingForClick = false;
      this.overlayRef.current?.fadeInBackground();

      this.isHandlingUserClick = false;
      this.canSkipAnimation = false;

      if (this.skipProtectionTimeout) { clearTimeout(this.skipProtectionTimeout); this.skipProtectionTimeout = null; }
      if (this.debounceTimeout) { clearTimeout(this.debounceTimeout); this.debounceTimeout = null; }

      // Fade out RC background music, play outro sound
      // TODO: Replace with RC-specific sounds when available
      soundManager.fadeOut(SOUND_NAME.free_spin_background_music, 500);
      soundManager.play(SOUND_NAME.free_spin_outro);

      if (this.outroSpine && this.symbolHeight && this.symbolHeight > 0 && this.dimensions) {
        this.applyScaleAndCenter();
      }

      // Clear state — clearTracks → clearListeners → apply → setToSetupPose
      this.outroSpine.state.clearTracks();
      this.outroSpine.state.clearListeners();
      this.outroSpine.state.apply(this.outroSpine.skeleton);
      this.outroSpine.skeleton.setToSetupPose();

      // SKIP PROTECTION: Block skipping for the first second
      this.skipProtectionTimeout = setTimeout(() => {
        if (this.isPlaying) {
          this.canSkipAnimation = true;
          this.showButton();
          this.isWaitingForClick = true;
        }
      }, 1000);

      // Play outro_in animation while STILL HIDDEN
      this.outroSpine.state.setAnimation(0, "free_spin_outro_in", false);

      // Show on NEXT FRAME
      if (this._showRAF) cancelAnimationFrame(this._showRAF);
      this._showRAF = requestAnimationFrame(() => {
        this._showRAF = null;
        if (this.isPlaying && this.outroSpine) {
          this.outroSpine.alpha = 1;
        }
      });

      const inDuration = this.getSpineAnimationDuration("free_spin_outro_in");

      const inTimeout = setTimeout(() => {
        if (this.isPlaying) {
          this.outroSpine.state.setAnimation(0, "free_spin_outro_idle", true);
          resolve();
        }
      }, inDuration);

      this.currentTimeout = inTimeout;
    });
  }

  // ===================================================================
  //  USER INTERACTION
  // ===================================================================

  handleUserClick() {
    if (!this.isPlaying || !this.outroSpine?.state) return false;
    if (this.debounceTimeout) return false;
    if (!this.canSkipAnimation) return false;
    if (!this.isWaitingForClick) return false;

    this.debounceTimeout = setTimeout(() => {
      this.debounceTimeout = null;
    }, 300);

    this.playClosingAnimation();
    return true;
  }

  playClosingAnimation() {
    if (!this.isWaitingForClick || !this.outroSpine?.state) return;

    this.isWaitingForClick = false;
    this.hideButton();

    // Stop outro sound and fade in base game music
    soundManager.stop(SOUND_NAME.free_spin_outro);
    soundManager.currentBackgroundMusic = SOUND_NAME.background_music;
    soundManager.fadeIn(SOUND_NAME.background_music, 500);

    this.outroSpine.state.clearListeners();

    this.outroSpine.state.setAnimation(0, "free_spin_outro_out", false);
    this.outroSpine.state.addListener({
      complete: (entry) => {
        if (entry.animation.name === "free_spin_outro_out" && this.isPlaying) {
          this.finishAnimation();
        }
      },
    });
  }

  finishAnimation() {
    if (!this.outroSpine || !this.initialized) return;

    this.isPlaying = false;
    this.isWaitingForClick = false;
    this.outroSpine.alpha = 0.001;

    if (this._showRAF) {
      cancelAnimationFrame(this._showRAF);
      this._showRAF = null;
    }

    soundManager.stop(SOUND_NAME.free_spin_outro);

    if (this.currentTimeout) { clearTimeout(this.currentTimeout); this.currentTimeout = null; }
    if (this.skipProtectionTimeout) { clearTimeout(this.skipProtectionTimeout); this.skipProtectionTimeout = null; }
    if (this.debounceTimeout) { clearTimeout(this.debounceTimeout); this.debounceTimeout = null; }

    this.isHandlingUserClick = false;
    this.canSkipAnimation = false;

    // CRITICAL: Close the dialog FIRST (synchronous) to prevent re-trigger.
    // The scene transition will reset royalCollectCurrentRound, which would
    // re-fire the startSequence effect if showRoyalCollectOutro is still true.
    this.dispatch(setShowRoyalCollectOutro(false));

    // Now trigger the scene transition (gradient fade covers screen → midpoint clears feature → reveal base game)
    this.dispatch(setRoyalCollectOutroTransitionActive(true));

    // Fade out the overlay (purely visual — dialog is already closed)
    this.overlayRef.current?.fadeOutBackground();
  }

  // ===================================================================
  //  CLEANUP
  // ===================================================================

  cleanup() {
    if (this._showRAF) { cancelAnimationFrame(this._showRAF); this._showRAF = null; }

    soundManager.stop(SOUND_NAME.free_spin_outro);

    if (this.currentTimeout) { clearTimeout(this.currentTimeout); this.currentTimeout = null; }
    if (this.skipProtectionTimeout) { clearTimeout(this.skipProtectionTimeout); this.skipProtectionTimeout = null; }
    if (this.debounceTimeout) { clearTimeout(this.debounceTimeout); this.debounceTimeout = null; }

    if (this.outroSpine?.removeSlotObjects) {
      this.outroSpine.removeSlotObjects();
    }

    if (this.buttonContainer) {
      this.buttonContainer.destroy({ children: true });
      this.buttonContainer = null;
      this.buttonShadowText = null;
      this.buttonText = null;
    }

    if (this.textContainers) {
      for (const container of this.textContainers) {
        container.destroy({ children: true });
      }
      this.textContainers = null;
    }

    if (this.outroSpine) {
      this.outroSpine.state?.clearListeners();
      this.outroSpine.state?.clearTracks();
      if (this.outroSpine.state) this.outroSpine.state.timeScale = 1.0;

      if (this.container?.children.includes(this.outroSpine)) {
        this.container.removeChild(this.outroSpine);
      }
      this.outroSpine.destroy();
      this.outroSpine = null;
    }

    this.container = null;
    this.initialized = false;
  }
}

export default RoyalCollectOutroDialogController;
